import {
  Box,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TextField,
  MenuItem,
  Select,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid2,
  IconButton,
  FormControl,
  InputLabel,
  useMediaQuery,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import moment from "moment";
import React, { useState, useCallback, useEffect } from "react";
import { Field, useField, useForm } from "react-final-form";
import ClearIcon from "@mui/icons-material/Clear";
import CopyIcon from "@mui/icons-material/ContentCopy";
import { FieldArray } from "react-final-form-arrays";
import History from "../ClientPortal/History";
import VideocamIcon from "@mui/icons-material/Videocam";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckIcon from "@mui/icons-material/Check";
import ExerciseCommentsSection from "./ExerciseCommentsSection";

const EXERCISE_TYPES = {
  SINGLE: "single",
  CIRCUIT: "circuit",
};

const FIELD_OPTIONS = [
  "REPS",
  "WEIGHT",
  "TIME",
  "DISTANCE",
  "DEGREE",
  "RPE",
  "BACKOFF",
];

const orderPrimaryWithRpeLast = (primary) => {
  if (!Array.isArray(primary) || primary.length === 0) return primary;
  const upper = (f) =>
    typeof f === "string" ? f.toUpperCase() : String(f).toUpperCase();
  const rpe = primary.find((f) => upper(f) === "RPE");
  const rest = primary.filter((f) => upper(f) !== "RPE");
  return rpe ? [...rest, rpe] : rest;
};

const DISTANCE_UNITS = [
  { value: "m", label: "m" },
  { value: "ft", label: "ft" },
  { value: "km", label: "km" },
  { value: "mi", label: "mi" },
  { value: "yd", label: "yd" },
];

// Normalize string for fuzzy matching: lowercase, trim, collapse spaces
const normalizeForMatch = (s) =>
  (s || "").toLowerCase().trim().replace(/\s+/g, " ");

// Fuzzy match exercise name against content library videos. Returns best matching video or null.
const findBestMatchingVideo = (exerciseName, videos) => {
  if (!exerciseName || !Array.isArray(videos) || videos.length === 0)
    return null;
  const normalized = normalizeForMatch(exerciseName);
  if (!normalized) return null;

  let best = null;
  let bestScore = 0;

  videos.forEach((video) => {
    const name = video.name || "";
    const normalizedVideo = normalizeForMatch(name);
    if (!normalizedVideo) return;

    let score = 0;
    if (normalizedVideo.includes(normalized)) {
      // Video name contains exercise name (e.g. "Barbell Back Squat" matches "Back Squat")
      score = 100 - normalizedVideo.length * 0.5;
    } else if (normalized.includes(normalizedVideo)) {
      // Exercise name contains video name
      score = 80 - normalizedVideo.length * 0.3;
    } else {
      // Word overlap: how many exercise words appear in video name
      const words = normalized.split(/\s+/).filter(Boolean);
      const matchCount = words.filter((w) =>
        normalizedVideo.includes(w)
      ).length;
      if (matchCount > 0) score = 20 + matchCount * 15;
    }

    if (score > bestScore) {
      bestScore = score;
      best = video;
    }
  });

  return bestScore >= 20 ? best : null;
};

// Resolve display name for a video key from content library, or fall back to filename
const getVideoDisplayName = (videoKey, videos) => {
  if (!videoKey) return "";
  const video = Array.isArray(videos)
    ? videos.find((v) => v.videoKey === videoKey)
    : null;
  return video?.name || videoKey.split("/").pop() || videoKey;
};

// Exercise-level fields (max 3 primary + BACKOFF). Empty array = intentional.
const getExerciseFields = (exercise) => {
  if (Array.isArray(exercise?.fields)) {
    const hasBackoff = exercise.fields.some((f) => f === "BACKOFF");
    let primary = exercise.fields.filter((f) => f !== "BACKOFF");
    const hasStrengthField =
      primary.includes("RPE") || primary.includes("WEIGHT");
    const isPureTimedOrMeasured =
      primary.includes("TIME") ||
      primary.includes("DISTANCE") ||
      primary.includes("DEGREE");
    if (
      hasStrengthField &&
      !isPureTimedOrMeasured &&
      !primary.includes("REPS")
    ) {
      primary = ["REPS", ...primary];
    }
    primary = orderPrimaryWithRpeLast(primary.slice(0, 3));
    return hasBackoff ? [...primary, "BACKOFF"] : primary;
  }
  const firstSet = exercise?.sets?.[0];
  if (
    firstSet &&
    (firstSet.type != null ||
      (Array.isArray(firstSet.types) && firstSet.types.length > 0))
  ) {
    if (Array.isArray(firstSet.types) && firstSet.types.length > 0)
      return orderPrimaryWithRpeLast(firstSet.types.slice(0, 3));
    if (firstSet.type === "TIME") return ["TIME"];
    if (firstSet.type === "DEGREE") return ["DEGREE"];
    if (firstSet.type === "RPE") return ["REPS", "WEIGHT", "RPE"];
    return ["REPS", "WEIGHT", "RPE"];
  }
  return ["REPS", "WEIGHT", "RPE"];
};

const AddExercise = ({
  index,
  removeExercise,
  inline = false,
  onOpenVideoModal,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
  contentLibraryVideos = [],
  userId,
  workoutId,
  expandCommentsSection = false,
  onOpenHistory,
}) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const form = useForm();

  // Video suggestion from content library fuzzy match (show on blur of exercise name)
  const [videoSuggestion, setVideoSuggestion] = useState(null);

  // Get the current video value for this exercise using useField
  const { input: videoInput } = useField(`exercises[${index}].video`, {
    subscription: { value: true },
  });
  const currentVideoUrl = videoInput.value;

  const videos = Array.isArray(contentLibraryVideos)
    ? contentLibraryVideos
    : [];

  const handleExerciseNameBlur = useCallback(() => {
    const name = form.getState().values?.exercises?.[index]?.name?.trim();
    if (!name || currentVideoUrl || videos.length === 0) return;
    const match = findBestMatchingVideo(name, videos);
    if (match && match.videoKey && match.name) {
      setVideoSuggestion({ videoKey: match.videoKey, name: match.name });
    } else {
      setVideoSuggestion(null);
    }
  }, [form, index, currentVideoUrl, videos]);

  // When opening a workout for edit, load suggestions for exercises that have a name but no video
  useEffect(() => {
    if (videos.length === 0) return;
    const timer = setTimeout(() => {
      const values = form.getState().values;
      const name = values?.exercises?.[index]?.name?.trim();
      const video = values?.exercises?.[index]?.video;
      if (!name || video) return;
      const match = findBestMatchingVideo(name, videos);
      if (match?.videoKey && match?.name) {
        setVideoSuggestion({ videoKey: match.videoKey, name: match.name });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [index, form, videos]);

  const defaultSet = {
    reps: "",
    weight: "",
    rpe: "",
    time: "",
    timeUnit: "sec",
    distance: "",
    degree: "",
    weightPerSide: false,
    repsPerSide: false,
    backoffPercent: "",
    backoffFromIndex: null,
  };

  const { input: fieldsInput } = useField(`exercises[${index}].fields`, {
    subscription: { value: true },
  });

  // Allow BACKOFF in addition to 3 primary fields.
  // We cap primary fields (non-BACKOFF) at 3, but BACKOFF does not count toward that limit.
  const rawExerciseFields =
    Array.isArray(fieldsInput.value) && fieldsInput.value.length > 0
      ? fieldsInput.value
      : getExerciseFields(form.getState().values?.exercises?.[index] || {});
  const basePrimaryFields = rawExerciseFields.filter(
    (f) => f && f.toUpperCase() !== "BACKOFF"
  );
  const limitedPrimaryFields = basePrimaryFields.slice(0, 3);
  const hasBackoffField = rawExerciseFields.some(
    (f) => f && f.toUpperCase() === "BACKOFF"
  );
  const exerciseFields = hasBackoffField
    ? [...limitedPrimaryFields, "BACKOFF"]
    : limitedPrimaryFields;

  return (
    <Box>
      <Grid2
        display="flex"
        sx={{ mb: 2 }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            aria-label="Move exercise up"
            disabled={!canMoveUp}
            onClick={onMoveUp}
          >
            <ArrowUpwardIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Move exercise down"
            disabled={!canMoveDown}
            onClick={onMoveDown}
          >
            <ArrowDownwardIcon fontSize="inherit" />
          </IconButton>
          <Typography sx={{ ml: 1 }}>Exercise {index + 1}</Typography>
        </Box>
        <ClearIcon color={"warning"} onClick={removeExercise} />
      </Grid2>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Field
          name={`exercises[${index}].name`}
          render={({ input }) => (
            <TextField
              {...input}
              label="Exercise Name"
              size="small"
              fullWidth
              onBlur={(e) => {
                input.onBlur?.(e);
                handleExerciseNameBlur();
              }}
            />
          )}
        />
        <IconButton
          color="primary"
          aria-label="view history"
          onClick={() =>
            onOpenHistory?.(
              form.getState().values?.exercises?.[index]?.name?.trim() ||
                "Exercise"
            )
          }
          size="small"
        >
          <ShowChartIcon />
        </IconButton>
        <IconButton
          color="primary"
          aria-label="add video"
          onClick={() => onOpenVideoModal(index)}
          size="small"
        >
          <VideocamIcon />
        </IconButton>
      </Box>
      {videoSuggestion && !currentVideoUrl && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "action.hover",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Video suggestion: {videoSuggestion.name}
          </Typography>
          <IconButton
            size="small"
            color="error"
            aria-label="dismiss suggestion"
            onClick={() => setVideoSuggestion(null)}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            aria-label="confirm and add this video"
            onClick={() => {
              videoInput.onChange(videoSuggestion.videoKey);
              setVideoSuggestion(null);
            }}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            aria-label="select a different video"
            onClick={() => {
              setVideoSuggestion(null);
              onOpenVideoModal(index);
            }}
          >
            <VideocamIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Field
          name={`exercises[${index}].restTime`}
          render={({ input }) => (
            <TextField
              {...input}
              label="Rest Time (seconds)"
              type="number"
              size="small"
              sx={{ width: 200 }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              onWheel={(e) => e.target.blur()} // prevent scroll from changing value
            />
          )}
        />
        <Field
          name={`exercises[${index}].weightUnit`}
          render={({ input }) => (
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>Weight Unit</InputLabel>
              <Select {...input} label="Weight Unit" defaultValue="lbs">
                <MenuItem value="lbs">Pounds (lbs)</MenuItem>
                <MenuItem value="kg">Kilograms (kg)</MenuItem>
              </Select>
            </FormControl>
          )}
        />
        <Field
          name={`exercises[${index}].exerciseType`}
          subscription={{ value: true }}
          render={({ input: typeInput }) => {
            const exerciseType = typeInput.value ?? EXERCISE_TYPES.SINGLE;
            return (
              <>
                <FormControl size="small" sx={{ width: 200 }}>
                  <InputLabel>Exercise Type</InputLabel>
                  <Select
                    {...typeInput}
                    value={exerciseType}
                    label="Exercise Type"
                    onChange={(e) => typeInput.onChange(e.target.value)}
                  >
                    <MenuItem value={EXERCISE_TYPES.SINGLE}>Single</MenuItem>
                    <MenuItem value={EXERCISE_TYPES.CIRCUIT}>Circuit</MenuItem>
                  </Select>
                </FormControl>
                {exerciseType !== EXERCISE_TYPES.CIRCUIT && (
                  <Field
                    name={`exercises[${index}].fields`}
                    subscription={{ value: true }}
                    render={({ input }) => {
                      const selected =
                        Array.isArray(input.value) && input.value.length > 0
                          ? input.value
                          : exerciseFields;
                      return (
                        <FormControl size="small" sx={{ width: 180 }}>
                          <InputLabel>Fields (max 3 + BACKOFF)</InputLabel>
                          <Select
                            multiple
                            {...input}
                            value={selected}
                            label="Fields (max 3 + BACKOFF)"
                            onChange={(e) => {
                              const incoming = Array.from(
                                new Set(e.target.value)
                              );
                              const hasBackoff = incoming.includes("BACKOFF");
                              const primary = incoming.filter(
                                (v) => v !== "BACKOFF"
                              );
                              const limitedPrimary = primary.slice(0, 3);
                              const next = hasBackoff
                                ? [...limitedPrimary, "BACKOFF"]
                                : limitedPrimary;
                              input.onChange(next);
                            }}
                            renderValue={(vals) => (vals || []).join(", ")}
                          >
                            {FIELD_OPTIONS.map((option) => (
                              <MenuItem
                                key={option}
                                value={option}
                                disabled={
                                  option !== "BACKOFF" &&
                                  !selected.includes(option) &&
                                  selected.filter((v) => v !== "BACKOFF")
                                    .length >= 3
                                }
                              >
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    }}
                  />
                )}
                {exerciseType !== EXERCISE_TYPES.CIRCUIT &&
                  exerciseFields.includes("DISTANCE") && (
                    <Field
                      name={`exercises[${index}].distanceUnit`}
                      subscription={{ value: true }}
                      render={({ input }) => (
                        <FormControl size="small" sx={{ width: 120 }}>
                          <InputLabel>Distance unit</InputLabel>
                          <Select
                            {...input}
                            value={input.value || "m"}
                            label="Distance unit"
                          >
                            {DISTANCE_UNITS.map((u) => (
                              <MenuItem key={u.value} value={u.value}>
                                {u.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  )}
              </>
            );
          }}
        />
      </Box>
      {currentVideoUrl && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: "block", mb: 1 }}
          >
            Video: {getVideoDisplayName(currentVideoUrl, videos)}
          </Typography>
          <IconButton
            color="error"
            aria-label="remove video"
            onClick={() => {
              videoInput.onChange(""); // Clear the video field
            }}
            size="small"
          >
            <ClearIcon />
          </IconButton>
        </Box>
      )}
      <Field
        subscription={{ value: true }}
        name={`exercises[${index}].description`}
        render={({ input }) => (
          <TextField
            fullWidth
            multiline
            size="small"
            sx={{ mt: 2 }}
            label="Exercise description"
            value={input.value}
            onChange={input.onChange}
          />
        )}
      />
      {workoutId ? (
        <ExerciseCommentsSection
          workoutId={workoutId}
          exerciseIndex={index}
          defaultExpanded={expandCommentsSection}
        />
      ) : null}
      <Field
        name={`exercises[${index}].exerciseType`}
        subscription={{ value: true }}
        render={({ input: typeInput }) => {
          const isCircuit = typeInput.value === EXERCISE_TYPES.CIRCUIT;
          return (
            <>
              {isCircuit ? null : (
                <FieldArray
                  name={`exercises[${index}].sets`}
                  render={({ fields }) => {
                    const activeFields = exerciseFields;
                    const hasBackoffField = activeFields.includes("BACKOFF");

                    return (
                      <>
                        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                          <Button
                            size={inline ? "small" : "medium"}
                            onClick={() => {
                              const lastSet =
                                fields.length > 0
                                  ? fields.value[fields.length - 1]
                                  : null;
                              fields.push(
                                lastSet
                                  ? { ...defaultSet, ...lastSet }
                                  : defaultSet
                              );
                            }}
                          >
                            Add Set
                          </Button>
                          {fields.length > 0 && (
                            <Button
                              size={inline ? "small" : "medium"}
                              onClick={() => {
                                const lastSet = fields.value[fields.length - 1];
                                fields.push({
                                  ...lastSet,
                                });
                              }}
                            >
                              Copy Last Set
                            </Button>
                          )}
                        </Box>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {!isMobile && (
                                <TableCell sx={{ padding: "5px" }}>#</TableCell>
                              )}
                              {activeFields.includes("REPS") && (
                                <TableCell>Reps (each)</TableCell>
                              )}
                              {activeFields.includes("WEIGHT") && (
                                <TableCell>Weight (each)</TableCell>
                              )}
                              {activeFields.includes("TIME") && (
                                <TableCell>Time (sec)</TableCell>
                              )}
                              {activeFields.includes("DISTANCE") && (
                                <TableCell>Distance</TableCell>
                              )}
                              {activeFields.includes("DEGREE") && (
                                <TableCell>Degree</TableCell>
                              )}
                              {hasBackoffField && (
                                <TableCell>Back Off</TableCell>
                              )}
                              {activeFields.includes("RPE") && (
                                <TableCell>RPE</TableCell>
                              )}
                              <TableCell></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {fields.map((name, i) => (
                              <SetActions
                                key={name}
                                exerciseIndex={index}
                                setIndex={i}
                                onRemove={() => fields.remove(i)}
                                fields={fields}
                                activeFields={activeFields}
                                hasBackoffField={hasBackoffField}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    );
                  }}
                />
              )}

              {isCircuit && (
                <FieldArray
                  name={`exercises[${index}].circuit`}
                  render={({ fields: roundFields }) => {
                    // Deep clone the last round's exercises
                    const cloneRound = (round) => {
                      if (!Array.isArray(round) || round.length === 0) {
                        return [];
                      }
                      return round.map((ex) => ({
                        name: ex.name || "",
                        description: ex.description || "",
                        video: ex.video || "",
                        fields: orderPrimaryWithRpeLast(
                          (Array.isArray(ex.fields)
                            ? ex.fields
                            : ["REPS", "WEIGHT", "RPE"]
                          ).slice(0, 3)
                        ),
                        distanceUnit: ex.distanceUnit || "m",
                        sets: (ex.sets || []).map((set) => ({
                          reps: set.reps ?? "",
                          weight: set.weight ?? "",
                          rpe: set.rpe ?? "",
                          time: set.time ?? "",
                          timeUnit: set.timeUnit || "sec",
                          distance: set.distance ?? "",
                          weightPerSide: set.weightPerSide ?? false,
                          repsPerSide: set.repsPerSide ?? false,
                          backoffPercent: set.backoffPercent ?? "",
                          backoffFromIndex:
                            typeof set.backoffFromIndex === "number"
                              ? set.backoffFromIndex
                              : null,
                        })),
                      }));
                    };

                    const handleAddRound = () => {
                      const circuitValues = roundFields.value || [];
                      const lastRound =
                        circuitValues.length > 0
                          ? circuitValues[circuitValues.length - 1]
                          : [];

                      // If no rounds exist, create an empty round
                      if (circuitValues.length === 0) {
                        roundFields.push([]);
                      } else {
                        // Copy the last round
                        roundFields.push(cloneRound(lastRound));
                      }
                    };

                    // Ensure at least one round exists
                    if (roundFields.length === 0) {
                      roundFields.push([]);
                    }

                    return (
                      <Box sx={{ mt: 3 }}>
                        {roundFields.map((roundName, roundIndex) => (
                          <Card
                            key={roundName}
                            variant="outlined"
                            sx={{ mb: 2 }}
                          >
                            <CardHeader
                              title={`Round ${roundIndex + 1}`}
                              action={
                                roundFields.length > 1 ? (
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() =>
                                      roundFields.remove(roundIndex)
                                    }
                                    aria-label="remove round"
                                  >
                                    <ClearIcon />
                                  </IconButton>
                                ) : null
                              }
                              sx={{ pb: 0 }}
                            />
                            <CardContent sx={{ pt: 1 }}>
                              <FieldArray
                                name={roundName}
                                render={({ fields: circuitExerciseFields }) => {
                                  const addExercise = () =>
                                    circuitExerciseFields.push({
                                      name: "",
                                      description: "",
                                      video: "",
                                      fields: ["REPS", "WEIGHT", "RPE"],
                                      distanceUnit: "m",
                                      sets: [
                                        {
                                          reps: "",
                                          weight: "",
                                          rpe: "",
                                          time: "",
                                          timeUnit: "sec",
                                          distance: "",
                                          weightPerSide: false,
                                          repsPerSide: false,
                                          backoffPercent: "",
                                          backoffFromIndex: null,
                                        },
                                      ],
                                    });
                                  return (
                                    <>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {circuitExerciseFields.length === 0
                                            ? "No circuit exercises yet."
                                            : `${
                                                circuitExerciseFields.length
                                              } exercise${
                                                circuitExerciseFields.length !==
                                                1
                                                  ? "s"
                                                  : ""
                                              }`}
                                        </Typography>
                                        <Button
                                          size="small"
                                          onClick={addExercise}
                                        >
                                          Add Exercise
                                        </Button>
                                      </Box>
                                      {circuitExerciseFields.map(
                                        (subName, subIndex) => (
                                          <Box
                                            key={subName}
                                            sx={{
                                              borderBottom:
                                                subIndex ===
                                                circuitExerciseFields.length - 1
                                                  ? "none"
                                                  : "1px solid #e0e0e0",
                                              pb: 1.5,
                                              mb: 1.5,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 1,
                                              }}
                                            >
                                              <Field
                                                name={`${subName}.name`}
                                                render={({ input }) => (
                                                  <TextField
                                                    {...input}
                                                    label={`Circuit Exercise ${
                                                      subIndex + 1
                                                    }`}
                                                    size="small"
                                                    fullWidth
                                                  />
                                                )}
                                              />
                                              <IconButton
                                                color="primary"
                                                aria-label="add video"
                                                onClick={() =>
                                                  onOpenVideoModal(
                                                    index,
                                                    roundIndex,
                                                    subIndex
                                                  )
                                                }
                                                size="small"
                                              >
                                                <VideocamIcon />
                                              </IconButton>
                                              <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() =>
                                                  circuitExerciseFields.remove(
                                                    subIndex
                                                  )
                                                }
                                              >
                                                <ClearIcon />
                                              </IconButton>
                                            </Box>
                                            <Field
                                              name={`${subName}.video`}
                                              subscription={{ value: true }}
                                              render={({ input }) => {
                                                const circuitVideoUrl =
                                                  input.value;
                                                return circuitVideoUrl ? (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 1,
                                                      mb: 1,
                                                    }}
                                                  >
                                                    <Typography
                                                      variant="caption"
                                                      color="textSecondary"
                                                    >
                                                      Video:{" "}
                                                      {getVideoDisplayName(
                                                        circuitVideoUrl,
                                                        videos
                                                      )}
                                                    </Typography>
                                                    <IconButton
                                                      color="error"
                                                      aria-label="remove video"
                                                      onClick={() => {
                                                        input.onChange(""); // Clear the video field
                                                      }}
                                                      size="small"
                                                    >
                                                      <ClearIcon />
                                                    </IconButton>
                                                  </Box>
                                                ) : null;
                                              }}
                                            />
                                            <Field
                                              name={`${subName}.fields`}
                                              subscription={{ value: true }}
                                              render={({
                                                input: fieldsInput,
                                              }) => {
                                                const cf =
                                                  Array.isArray(
                                                    fieldsInput.value
                                                  ) &&
                                                  fieldsInput.value.length > 0
                                                    ? fieldsInput.value.slice(
                                                        0,
                                                        3
                                                      )
                                                    : ["REPS", "WEIGHT", "RPE"];
                                                return (
                                                  <FieldArray
                                                    name={`${subName}.sets`}
                                                    render={({
                                                      fields: subSetFields,
                                                    }) => (
                                                      <>
                                                        <Box
                                                          sx={{
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                            gap: 1,
                                                            mb: 1,
                                                          }}
                                                        >
                                                          <Typography variant="caption">
                                                            Fields:
                                                          </Typography>
                                                          <Field
                                                            name={`${subName}.fields`}
                                                            render={({
                                                              input,
                                                            }) => (
                                                              <Select
                                                                multiple
                                                                {...input}
                                                                size="small"
                                                                value={
                                                                  input.value ||
                                                                  cf
                                                                }
                                                                sx={{
                                                                  minWidth: 140,
                                                                }}
                                                                onChange={(
                                                                  e
                                                                ) => {
                                                                  const next =
                                                                    Array.from(
                                                                      new Set(
                                                                        e.target.value
                                                                      )
                                                                    );
                                                                  const hasBackoff =
                                                                    next.includes(
                                                                      "BACKOFF"
                                                                    );
                                                                  const primary =
                                                                    next.filter(
                                                                      (v) =>
                                                                        v !==
                                                                        "BACKOFF"
                                                                    );
                                                                  const limitedPrimary =
                                                                    orderPrimaryWithRpeLast(
                                                                      primary.slice(
                                                                        0,
                                                                        3
                                                                      )
                                                                    );
                                                                  const normalized =
                                                                    hasBackoff
                                                                      ? [
                                                                          ...limitedPrimary,
                                                                          "BACKOFF",
                                                                        ]
                                                                      : limitedPrimary;
                                                                  input.onChange(
                                                                    normalized
                                                                  );
                                                                }}
                                                                renderValue={(
                                                                  vals
                                                                ) =>
                                                                  (
                                                                    vals || []
                                                                  ).join(", ")
                                                                }
                                                              >
                                                                {FIELD_OPTIONS.map(
                                                                  (o) => (
                                                                    <MenuItem
                                                                      key={o}
                                                                      value={o}
                                                                      disabled={
                                                                        o !==
                                                                          "BACKOFF" &&
                                                                        !(
                                                                          input.value ||
                                                                          cf
                                                                        ).includes(
                                                                          o
                                                                        ) &&
                                                                        (
                                                                          input.value ||
                                                                          cf
                                                                        ).filter(
                                                                          (v) =>
                                                                            v !==
                                                                            "BACKOFF"
                                                                        )
                                                                          .length >=
                                                                          3
                                                                      }
                                                                    >
                                                                      {o}
                                                                    </MenuItem>
                                                                  )
                                                                )}
                                                              </Select>
                                                            )}
                                                          />
                                                          {cf.includes(
                                                            "DISTANCE"
                                                          ) && (
                                                            <Field
                                                              name={`${subName}.distanceUnit`}
                                                              subscription={{
                                                                value: true,
                                                              }}
                                                              render={({
                                                                input:
                                                                  unitInput,
                                                              }) => (
                                                                <Select
                                                                  {...unitInput}
                                                                  size="small"
                                                                  value={
                                                                    unitInput.value ||
                                                                    "m"
                                                                  }
                                                                  sx={{
                                                                    minWidth: 80,
                                                                  }}
                                                                  onChange={(
                                                                    e
                                                                  ) =>
                                                                    unitInput.onChange(
                                                                      e.target
                                                                        .value
                                                                    )
                                                                  }
                                                                >
                                                                  {DISTANCE_UNITS.map(
                                                                    (u) => (
                                                                      <MenuItem
                                                                        key={
                                                                          u.value
                                                                        }
                                                                        value={
                                                                          u.value
                                                                        }
                                                                      >
                                                                        {
                                                                          u.label
                                                                        }
                                                                      </MenuItem>
                                                                    )
                                                                  )}
                                                                </Select>
                                                              )}
                                                            />
                                                          )}
                                                        </Box>
                                                        {subSetFields.map(
                                                          (setPath) => (
                                                            <Table
                                                              key={setPath}
                                                              sx={{ mt: 1 }}
                                                            >
                                                              <TableHead>
                                                                <TableRow>
                                                                  {cf.includes(
                                                                    "REPS"
                                                                  ) ? (
                                                                    <TableCell>
                                                                      Reps
                                                                      (each)
                                                                    </TableCell>
                                                                  ) : null}
                                                                  {cf.includes(
                                                                    "WEIGHT"
                                                                  ) && (
                                                                    <TableCell>
                                                                      Weight
                                                                      (each)
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "TIME"
                                                                  ) && (
                                                                    <TableCell>
                                                                      Time (sec)
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "DISTANCE"
                                                                  ) && (
                                                                    <TableCell>
                                                                      Distance
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "DEGREE"
                                                                  ) && (
                                                                    <TableCell>
                                                                      Degree
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "BACKOFF"
                                                                  ) &&
                                                                    parseInt(
                                                                      setPath
                                                                        .split(
                                                                          "."
                                                                        )
                                                                        .pop() ||
                                                                        "0",
                                                                      10
                                                                    ) > 0 && (
                                                                      <TableCell>
                                                                        Back Off
                                                                      </TableCell>
                                                                    )}
                                                                  {cf.includes(
                                                                    "RPE"
                                                                  ) && (
                                                                    <TableCell>
                                                                      RPE
                                                                    </TableCell>
                                                                  )}
                                                                </TableRow>
                                                              </TableHead>
                                                              <TableBody>
                                                                <TableRow>
                                                                  {cf.includes(
                                                                    "REPS"
                                                                  ) ? (
                                                                    <TableCell>
                                                                      <Box
                                                                        sx={{
                                                                          display:
                                                                            "flex",
                                                                          alignItems:
                                                                            "center",
                                                                          gap: 1,
                                                                        }}
                                                                      >
                                                                        <Field
                                                                          name={`${setPath}.reps`}
                                                                          render={({
                                                                            input,
                                                                          }) => (
                                                                            <TextField
                                                                              {...input}
                                                                              size="small"
                                                                              sx={{
                                                                                width: 80,
                                                                              }}
                                                                            />
                                                                          )}
                                                                        />
                                                                        <Field
                                                                          name={`${setPath}.repsPerSide`}
                                                                          subscription={{
                                                                            value: true,
                                                                          }}
                                                                          render={({
                                                                            input,
                                                                          }) => (
                                                                            <FormControlLabel
                                                                              control={
                                                                                <Checkbox
                                                                                  size="small"
                                                                                  checked={
                                                                                    !!input.value
                                                                                  }
                                                                                  onChange={(
                                                                                    e
                                                                                  ) =>
                                                                                    input.onChange(
                                                                                      e
                                                                                        .target
                                                                                        .checked
                                                                                    )
                                                                                  }
                                                                                />
                                                                              }
                                                                              sx={{
                                                                                m: 0,
                                                                              }}
                                                                            />
                                                                          )}
                                                                        />
                                                                      </Box>
                                                                    </TableCell>
                                                                  ) : (
                                                                    <TableCell>
                                                                      -
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "WEIGHT"
                                                                  ) && (
                                                                    <TableCell>
                                                                      <Field
                                                                        name={`${setPath}.backoffPercent`}
                                                                        subscription={{
                                                                          value: true,
                                                                        }}
                                                                        render={({
                                                                          input:
                                                                            pctInput,
                                                                        }) => (
                                                                          <Field
                                                                            name={`${setPath}.backoffFromIndex`}
                                                                            subscription={{
                                                                              value: true,
                                                                            }}
                                                                            render={({
                                                                              input:
                                                                                fromInput,
                                                                            }) => {
                                                                              const setIdx =
                                                                                parseInt(
                                                                                  setPath
                                                                                    .split(
                                                                                      "."
                                                                                    )
                                                                                    .pop() ||
                                                                                    "0",
                                                                                  10
                                                                                );
                                                                              const isBackoffSet =
                                                                                cf.includes(
                                                                                  "BACKOFF"
                                                                                ) &&
                                                                                setIdx >
                                                                                  0 &&
                                                                                pctInput.value !==
                                                                                  "" &&
                                                                                pctInput.value !=
                                                                                  null &&
                                                                                fromInput.value !=
                                                                                  null &&
                                                                                fromInput.value !==
                                                                                  "";
                                                                              if (
                                                                                isBackoffSet
                                                                              ) {
                                                                                return "-";
                                                                              }
                                                                              return (
                                                                                <Box
                                                                                  sx={{
                                                                                    display:
                                                                                      "flex",
                                                                                    alignItems:
                                                                                      "center",
                                                                                    gap: 1,
                                                                                  }}
                                                                                >
                                                                                  <Field
                                                                                    name={`${setPath}.weight`}
                                                                                    render={({
                                                                                      input,
                                                                                    }) => (
                                                                                      <TextField
                                                                                        {...input}
                                                                                        size="small"
                                                                                        sx={{
                                                                                          width: 80,
                                                                                        }}
                                                                                      />
                                                                                    )}
                                                                                  />
                                                                                  <Field
                                                                                    name={`${setPath}.weightPerSide`}
                                                                                    subscription={{
                                                                                      value: true,
                                                                                    }}
                                                                                    render={({
                                                                                      input,
                                                                                    }) => (
                                                                                      <FormControlLabel
                                                                                        control={
                                                                                          <Checkbox
                                                                                            size="small"
                                                                                            checked={
                                                                                              !!input.value
                                                                                            }
                                                                                            onChange={(
                                                                                              e
                                                                                            ) =>
                                                                                              input.onChange(
                                                                                                e
                                                                                                  .target
                                                                                                  .checked
                                                                                              )
                                                                                            }
                                                                                          />
                                                                                        }
                                                                                        sx={{
                                                                                          m: 0,
                                                                                        }}
                                                                                      />
                                                                                    )}
                                                                                  />
                                                                                </Box>
                                                                              );
                                                                            }}
                                                                          />
                                                                        )}
                                                                      />
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "TIME"
                                                                  ) && (
                                                                    <TableCell>
                                                                      <Field
                                                                        name={`${setPath}.time`}
                                                                        render={({
                                                                          input,
                                                                        }) => (
                                                                          <TextField
                                                                            {...input}
                                                                            size="small"
                                                                            sx={{
                                                                              width: 120,
                                                                            }}
                                                                            type="number"
                                                                            inputProps={{
                                                                              min: 0,
                                                                            }}
                                                                          />
                                                                        )}
                                                                      />
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "DISTANCE"
                                                                  ) && (
                                                                    <TableCell>
                                                                      <Field
                                                                        name={`${setPath}.distance`}
                                                                        render={({
                                                                          input,
                                                                        }) => (
                                                                          <TextField
                                                                            {...input}
                                                                            size="small"
                                                                            sx={{
                                                                              width: 100,
                                                                            }}
                                                                            type="number"
                                                                            inputProps={{
                                                                              min: 0,
                                                                              step: "any",
                                                                            }}
                                                                          />
                                                                        )}
                                                                      />
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "BACKOFF"
                                                                  ) && (
                                                                    <TableCell>
                                                                      {parseInt(
                                                                        setPath
                                                                          .split(
                                                                            "."
                                                                          )
                                                                          .pop() ||
                                                                          "0",
                                                                        10
                                                                      ) > 0 ? (
                                                                        <Box
                                                                          sx={{
                                                                            display:
                                                                              "flex",
                                                                            alignItems:
                                                                              "center",
                                                                            gap: 1,
                                                                          }}
                                                                        >
                                                                          <Field
                                                                            name={`${setPath}.backoffPercent`}
                                                                            render={({
                                                                              input,
                                                                            }) => {
                                                                              const basePath =
                                                                                setPath.replace(
                                                                                  /\[\d+\]$/,
                                                                                  ""
                                                                                );
                                                                              const currentSetIdx =
                                                                                parseInt(
                                                                                  setPath.match(
                                                                                    /(\d+)$/
                                                                                  )?.[1] ??
                                                                                    "0",
                                                                                  10
                                                                                );
                                                                              return (
                                                                                <TextField
                                                                                  {...input}
                                                                                  size="small"
                                                                                  sx={{
                                                                                    width: 70,
                                                                                  }}
                                                                                  label="% Off"
                                                                                  type="number"
                                                                                  inputProps={{
                                                                                    min: 0,
                                                                                    max: 100,
                                                                                  }}
                                                                                  onChange={(
                                                                                    e
                                                                                  ) => {
                                                                                    const value =
                                                                                      e
                                                                                        .target
                                                                                        .value;
                                                                                    input.onChange(
                                                                                      value
                                                                                    );
                                                                                    for (
                                                                                      let i =
                                                                                        currentSetIdx +
                                                                                        1;
                                                                                      i <
                                                                                      subSetFields.length;
                                                                                      i++
                                                                                    ) {
                                                                                      form.change(
                                                                                        `${basePath}[${i}].backoffPercent`,
                                                                                        value
                                                                                      );
                                                                                    }
                                                                                  }}
                                                                                />
                                                                              );
                                                                            }}
                                                                          />
                                                                          <Field
                                                                            name={`${setPath}.backoffFromIndex`}
                                                                            subscription={{
                                                                              value: true,
                                                                            }}
                                                                            render={({
                                                                              input,
                                                                            }) => {
                                                                              const currentIndex =
                                                                                parseInt(
                                                                                  setPath
                                                                                    .split(
                                                                                      "."
                                                                                    )
                                                                                    .pop() ||
                                                                                    "0",
                                                                                  10
                                                                                );
                                                                              const basePath =
                                                                                setPath.replace(
                                                                                  /\[\d+\]$/,
                                                                                  ""
                                                                                );
                                                                              const options =
                                                                                Array.from(
                                                                                  {
                                                                                    length:
                                                                                      currentIndex,
                                                                                  },
                                                                                  (
                                                                                    _,
                                                                                    i
                                                                                  ) =>
                                                                                    i
                                                                                );
                                                                              return (
                                                                                <FormControl
                                                                                  size="small"
                                                                                  sx={{
                                                                                    minWidth: 80,
                                                                                  }}
                                                                                >
                                                                                  <InputLabel>
                                                                                    From
                                                                                    Set
                                                                                  </InputLabel>
                                                                                  <Select
                                                                                    {...input}
                                                                                    label="From Set"
                                                                                    value={
                                                                                      input.value ===
                                                                                        null ||
                                                                                      input.value ===
                                                                                        undefined
                                                                                        ? ""
                                                                                        : input.value
                                                                                    }
                                                                                    onChange={(
                                                                                      e
                                                                                    ) => {
                                                                                      const value =
                                                                                        e
                                                                                          .target
                                                                                          .value;
                                                                                      const parsed =
                                                                                        value ===
                                                                                        ""
                                                                                          ? null
                                                                                          : parseInt(
                                                                                              value,
                                                                                              10
                                                                                            );
                                                                                      input.onChange(
                                                                                        parsed
                                                                                      );
                                                                                      for (
                                                                                        let i =
                                                                                          currentIndex +
                                                                                          1;
                                                                                        i <
                                                                                        subSetFields.length;
                                                                                        i++
                                                                                      ) {
                                                                                        form.change(
                                                                                          `${basePath}[${i}].backoffFromIndex`,
                                                                                          parsed
                                                                                        );
                                                                                      }
                                                                                    }}
                                                                                  >
                                                                                    <MenuItem value="">
                                                                                      <em>
                                                                                        None
                                                                                      </em>
                                                                                    </MenuItem>
                                                                                    {options.map(
                                                                                      (
                                                                                        i
                                                                                      ) => (
                                                                                        <MenuItem
                                                                                          key={
                                                                                            i
                                                                                          }
                                                                                          value={
                                                                                            i
                                                                                          }
                                                                                        >
                                                                                          {`Set ${
                                                                                            i +
                                                                                            1
                                                                                          }`}
                                                                                        </MenuItem>
                                                                                      )
                                                                                    )}
                                                                                  </Select>
                                                                                </FormControl>
                                                                              );
                                                                            }}
                                                                          />
                                                                        </Box>
                                                                      ) : (
                                                                        "-"
                                                                      )}
                                                                    </TableCell>
                                                                  )}
                                                                  {cf.includes(
                                                                    "RPE"
                                                                  ) && (
                                                                    <TableCell>
                                                                      <Field
                                                                        name={`${setPath}.rpe`}
                                                                        render={({
                                                                          input,
                                                                        }) => (
                                                                          <FormControl size="small">
                                                                            <Select
                                                                              {...input}
                                                                              sx={{
                                                                                width: 120,
                                                                              }}
                                                                            >
                                                                              <MenuItem value="">
                                                                                <em>
                                                                                  -
                                                                                </em>
                                                                              </MenuItem>
                                                                              <MenuItem value="6">
                                                                                6
                                                                              </MenuItem>
                                                                              <MenuItem value="7">
                                                                                7
                                                                              </MenuItem>
                                                                              <MenuItem value="8">
                                                                                8
                                                                              </MenuItem>
                                                                              <MenuItem value="9">
                                                                                9
                                                                              </MenuItem>
                                                                              <MenuItem value="10">
                                                                                10
                                                                              </MenuItem>
                                                                            </Select>
                                                                          </FormControl>
                                                                        )}
                                                                      />
                                                                    </TableCell>
                                                                  )}
                                                                </TableRow>
                                                              </TableBody>
                                                            </Table>
                                                          )
                                                        )}
                                                      </>
                                                    )}
                                                  />
                                                );
                                              }}
                                            />
                                          </Box>
                                        )
                                      )}
                                    </>
                                  );
                                }}
                              />
                            </CardContent>
                          </Card>
                        ))}
                        <Box
                          sx={{
                            mt: 2,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="outlined"
                            onClick={handleAddRound}
                            disabled={roundFields.length >= 10}
                            size="small"
                          >
                            Add Round
                          </Button>
                        </Box>
                      </Box>
                    );
                  }}
                />
              )}
            </>
          );
        }}
      />
    </Box>
  );
};

const SetActions = ({
  exerciseIndex,
  setIndex,
  onRemove,
  fields,
  activeFields,
  hasBackoffField = false,
}) => {
  const form = useForm();
  const isMobile = useMediaQuery("(max-width: 600px)");

  const handleWeightChange = (value) => {
    // Update current set's weight
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].weight`, value);

    // Update all sets below with the same weight
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].weight`, value);
    }
  };

  const handleWeightPerHandChange = (value) => {
    form.change(
      `exercises[${exerciseIndex}].sets[${setIndex}].weightPerSide`,
      value
    );
    // Update all sets below with the same weight per hand
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(
        `exercises[${exerciseIndex}].sets[${i}].weightPerSide`,
        value
      );
    }
  };

  const handleRepsPerSideChange = (value) => {
    form.change(
      `exercises[${exerciseIndex}].sets[${setIndex}].repsPerSide`,
      value
    );
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].repsPerSide`, value);
    }
  };

  const handleRpeChange = (value) => {
    // Update current set's RPE
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].rpe`, value);

    // Update all sets below with the same RPE
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].rpe`, value);
    }
  };

  const includesWeight = activeFields.includes("WEIGHT");
  const includesRpe = activeFields.includes("RPE");
  const includesTime = activeFields.includes("TIME");
  const includesDistance = activeFields.includes("DISTANCE");
  const includesDegree = activeFields.includes("DEGREE");
  const includesReps = activeFields.includes("REPS");

  const handleDistanceChange = (value) => {
    form.change(
      `exercises[${exerciseIndex}].sets[${setIndex}].distance`,
      value
    );
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].distance`, value);
    }
  };

  const handleDegreeChange = (value) => {
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].degree`, value);
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].degree`, value);
    }
  };

  const handleRepsChange = (value) => {
    // Update current set's reps
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].reps`, value);

    // Update all sets below with the same reps
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].reps`, value);
    }
  };

  const handleTimeChange = (value) => {
    form.change(`exercises[${exerciseIndex}].sets[${setIndex}].time`, value);

    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(`exercises[${exerciseIndex}].sets[${i}].time`, value);
    }
  };

  const handleBackoffPercentChange = (value) => {
    form.change(
      `exercises[${exerciseIndex}].sets[${setIndex}].backoffPercent`,
      value
    );
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(
        `exercises[${exerciseIndex}].sets[${i}].backoffPercent`,
        value
      );
    }
  };

  const handleBackoffFromIndexChange = (value) => {
    const parsed =
      value === "" || value === null || value === undefined
        ? null
        : parseInt(value, 10);
    form.change(
      `exercises[${exerciseIndex}].sets[${setIndex}].backoffFromIndex`,
      parsed
    );
    for (let i = setIndex + 1; i < fields.length; i++) {
      form.change(
        `exercises[${exerciseIndex}].sets[${i}].backoffFromIndex`,
        parsed
      );
    }
  };

  return (
    <TableRow>
      {!isMobile && (
        <TableCell sx={{ padding: "5px" }}>{setIndex + 1}</TableCell>
      )}
      {includesReps ? (
        <TableCell align="center">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Field
              name={`exercises[${exerciseIndex}].sets[${setIndex}].reps`}
              render={({ input }) => (
                <TextField
                  {...input}
                  variant="outlined"
                  size="small"
                  sx={{ width: 60 }}
                  onChange={(e) => handleRepsChange(e.target.value)}
                />
              )}
            />
            <Field
              name={`exercises[${exerciseIndex}].sets[${setIndex}].repsPerSide`}
              subscription={{ value: true }}
              render={({ input: checkboxInput }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!checkboxInput.value}
                      onChange={(e) =>
                        handleRepsPerSideChange(e.target.checked)
                      }
                    />
                  }
                  sx={{ m: 0 }}
                />
              )}
            />
          </Box>
        </TableCell>
      ) : null}
      {includesWeight && (
        <TableCell>
          <Field
            name={`exercises[${exerciseIndex}].sets[${setIndex}].backoffPercent`}
            subscription={{ value: true }}
            render={({ input: backoffPercentInput }) => (
              <Field
                name={`exercises[${exerciseIndex}].sets[${setIndex}].backoffFromIndex`}
                subscription={{ value: true }}
                render={({ input: backoffFromIndexInput }) => {
                  const isBackoffSet =
                    hasBackoffField &&
                    setIndex > 0 &&
                    backoffPercentInput.value !== "" &&
                    backoffPercentInput.value !== null &&
                    backoffFromIndexInput.value !== null &&
                    backoffFromIndexInput.value !== "";

                  if (isBackoffSet) {
                    return "-";
                  }

                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Field
                        name={`exercises[${exerciseIndex}].sets[${setIndex}].weight`}
                        render={({ input }) => (
                          <TextField
                            {...input}
                            variant="outlined"
                            size="small"
                            sx={{ width: 80 }}
                            label="Weight"
                            onChange={(e) => handleWeightChange(e.target.value)}
                          />
                        )}
                      />
                      <Field
                        name={`exercises[${exerciseIndex}].sets[${setIndex}].weightPerSide`}
                        subscription={{ value: true }}
                        render={({ input: checkboxInput }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={!!checkboxInput.value}
                                onChange={(e) =>
                                  handleWeightPerHandChange(e.target.checked)
                                }
                              />
                            }
                            sx={{ m: 0 }}
                          />
                        )}
                      />
                    </Box>
                  );
                }}
              />
            )}
          />
        </TableCell>
      )}
      {includesTime && (
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Field
              name={`exercises[${exerciseIndex}].sets[${setIndex}].time`}
              render={({ input }) => (
                <TextField
                  {...input}
                  variant="outlined"
                  size="small"
                  sx={{ width: 80 }}
                  label="Time"
                  type="number"
                  inputProps={{ min: 0 }}
                  onChange={(e) => handleTimeChange(e.target.value)}
                />
              )}
            />
            <Field
              name={`exercises[${exerciseIndex}].sets[${setIndex}].timeUnit`}
              subscription={{ value: true }}
              render={({ input }) => (
                <FormControl size="small" sx={{ minWidth: 70 }}>
                  <Select
                    {...input}
                    value={input.value || "sec"}
                    onChange={(e) => input.onChange(e.target.value)}
                  >
                    <MenuItem value="sec">sec</MenuItem>
                    <MenuItem value="min">min</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>
        </TableCell>
      )}
      {includesDistance && (
        <TableCell>
          <Field
            name={`exercises[${exerciseIndex}].sets[${setIndex}].distance`}
            render={({ input }) => (
              <TextField
                {...input}
                variant="outlined"
                size="small"
                sx={{ width: 90 }}
                type="number"
                inputProps={{ min: 0, step: "any" }}
                onChange={(e) => handleDistanceChange(e.target.value)}
              />
            )}
          />
        </TableCell>
      )}
      {includesDegree && (
        <TableCell>
          <Field
            name={`exercises[${exerciseIndex}].sets[${setIndex}].degree`}
            render={({ input }) => (
              <TextField
                {...input}
                variant="outlined"
                size="small"
                sx={{ width: 90 }}
                type="number"
                inputProps={{ step: "any" }}
                onChange={(e) => handleDegreeChange(e.target.value)}
              />
            )}
          />
        </TableCell>
      )}
      {hasBackoffField && (
        <TableCell>
          {setIndex > 0 ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Field
                name={`exercises[${exerciseIndex}].sets[${setIndex}].backoffPercent`}
                render={({ input }) => (
                  <TextField
                    {...input}
                    variant="outlined"
                    size="small"
                    sx={{ width: 70 }}
                    label="% Off"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    onChange={(e) => handleBackoffPercentChange(e.target.value)}
                  />
                )}
              />
              <Field
                name={`exercises[${exerciseIndex}].sets[${setIndex}].backoffFromIndex`}
                subscription={{ value: true }}
                render={({ input }) => {
                  const currentIndex = setIndex;
                  const options = Array.from(
                    { length: currentIndex },
                    (_, i) => i
                  );
                  return (
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <InputLabel>From Set</InputLabel>
                      <Select
                        {...input}
                        label="From Set"
                        value={
                          input.value === null || input.value === undefined
                            ? ""
                            : input.value
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          handleBackoffFromIndexChange(value);
                        }}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {options.map((i) => (
                          <MenuItem key={i} value={i}>
                            {`Set ${i + 1}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }}
              />
            </Box>
          ) : (
            "-"
          )}
        </TableCell>
      )}
      {includesRpe && (
        <TableCell>
          <Field
            name={`exercises[${exerciseIndex}].sets[${setIndex}].rpe`}
            render={({ input }) => (
              <FormControl size="small" sx={{ width: 100 }}>
                <Select
                  {...input}
                  onChange={(e) => handleRpeChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>-</em>
                  </MenuItem>
                  {["6", "7", "8", "9", "10"].map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </TableCell>
      )}
      <TableCell>
        <ClearIcon
          color={"warning"}
          onClick={onRemove}
          sx={{ cursor: "pointer" }}
        />
      </TableCell>
    </TableRow>
  );
};

export default AddExercise;
